// components/VariantTable.tsx
import React from "react";
import Fields from "@components/fields";
import { Product } from "@apis/index";
import { toast } from "react-toastify";
import { Helper } from "@utils/index";
import theme from "@utils/theme";
import { Percent, BadgeCent } from "lucide-react";

interface VariantTableProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  selectedColors: any[];
  selectedAttributes: any[];
  productData: any;
}

export const VariantTable: React.FC<VariantTableProps> = ({
  values,
  setFieldValue,
  selectedColors,
  selectedAttributes,
  productData,
}) => {
  if (!values.variants || values.variants.length === 0) {
    return null;
  }

  const removeVariant = async (variant: any, index: number) => {
    if (
      !!productData?.id &&
      variant.id &&
      !String(variant.id).includes("temp")
    ) {
      // This is an existing variant from DB, remove via API
      await Product.removeVariation({
        state: "products",
        id: variant.id,
      })
        .then(() => {
          const newVariants = [...values.variants];
          newVariants.splice(index, 1);
          setFieldValue("variants", newVariants);
          toast.success("Variant removed successfully");
        })
        .catch((err) => {
          toast.error("Failed to remove variant");
          console.error("Remove variant error:", err);
        });
    } else {
      // This is a newly created variant, just remove from state
      const newVariants = [...values.variants];
      newVariants.splice(index, 1);
      setFieldValue("variants", newVariants);
      toast.success("Variant removed successfully");
    }
  };

  const generateVariantSku = (variant: any, index: number) => {
    const variantName = variant.name || "variant";
    const attributesText = variant.attributes
      ? Object.values(variant.attributes).join("-")
      : "";
    const baseSku = `${variantName}${attributesText ? `-${attributesText}` : ""}`;
    const sku = Helper.generateSku(baseSku);
    setFieldValue(`variants.${index}.sku`, sku);
  };

  const applyMainDiscountToAll = () => {
    // values.discountType: "1" = Percentage, "2" = Flat (Amount) - logic derived from useProductSubmit
    const mainType = values.discountType === "1" ? "percentage" : "flat";
    const mainValue = values.discount;

    if (!mainValue) {
      toast.info(
        "Please set a discount value in the main product settings first."
      );
      return;
    }

    const newVariants = values.variants.map((v: any) => ({
      ...v,
      discountType: mainType,
      discount: mainValue,
    }));
    setFieldValue("variants", newVariants);
    toast.success("Applied product discount to all variants");
  };

  const clearAllDiscounts = () => {
    if (
      !confirm("Are you sure you want to remove discounts from all variants?")
    )
      return;
    const newVariants = values.variants.map((v: any) => ({
      ...v,
      discountType: "percentage", // Reset to default or null
      discount: "",
    }));
    setFieldValue("variants", newVariants);
    toast.success("Cleared all variant discounts");
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "10px",
          gap: "10px",
        }}
      >
        <button
          type="button"
          onClick={clearAllDiscounts}
          style={{
            background: "white",
            border: "1px solid #e2e8f0",
            padding: "6px 12px",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 500,
            color: "#64748b",
            transition: "all 0.2s",
          }}
        >
          Clear Discounts
        </button>
        <button
          type="button"
          onClick={applyMainDiscountToAll}
          style={{
            background: theme.colors.primary,
            border: "none",
            padding: "6px 12px",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 500,
            color: "white",
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            transition: "all 0.2s",
          }}
        >
          Apply Product Discount to All
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: 900,
          }}
        >
          <thead>
            <tr
              style={{
                textAlign: "left",
                borderBottom: "1px solid #eaeaea",
              }}
            >
              {selectedColors.length > 0 && <th style={{ padding: "10px" }}>Color</th>}
              {selectedAttributes.map((attr) => (
                <th key={attr.id} style={{ padding: "10px" }}>
                  {attr.name}
                </th>
              ))}
              <th style={{ padding: "10px" }}>Price</th>
              <th style={{ padding: "10px" }}>Discount</th>
              <th style={{ padding: "10px" }}>
                Stock
                <div style={{ fontSize: "10px", fontWeight: "normal", color: "#64748b" }}>
                  (Empty = Unlimited)
                </div>
              </th>
              <th style={{ padding: "10px" }}>SKU</th>
              <th style={{ padding: "10px" }}>Image</th>
              <th style={{ padding: "10px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {values.variants.map((variant: any, idx: number) => (
              <VariantRow
                key={variant.id ?? idx}
                variant={variant}
                index={idx}
                selectedColors={selectedColors}
                selectedAttributes={selectedAttributes}
                onRemove={() => removeVariant(variant, idx)}
                onGenerateSku={() => generateVariantSku(variant, idx)}
                setFieldValue={setFieldValue}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const VariantRow: React.FC<{
  variant: any;
  index: number;
  selectedColors: any[];
  selectedAttributes: any[];
  onRemove: () => void;
  onGenerateSku: () => void;
  setFieldValue: (field: string, value: any) => void;
}> = ({
  variant,
  index,
  selectedColors,
  selectedAttributes,
  onRemove,
  onGenerateSku,
  setFieldValue,
}) => {
  return (
    <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
      {selectedColors.length > 0 && (
        <td style={{ padding: "10px", verticalAlign: "middle" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: 4,
                background: variant.code,
                border: "1px solid #ccc",
              }}
            />
            <div>{variant.name}</div>
          </div>
        </td>
      )}

      {selectedAttributes.map((attr) => (
        <td
          key={attr.id}
          style={{
            padding: "10px",
            verticalAlign: "middle",
          }}
        >
          {variant.attributes ? (variant.attributes[attr.name] ?? "-") : "-"}
        </td>
      ))}

      <td style={{ padding: "8px 10px" }}>
        <Fields.Input name={`variants.${index}.price`} placeholder="Price" />
      </td>
      <td style={{ padding: "8px 10px", minWidth: "70px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            border: "1px solid #e2e8f0",
            borderRadius: "8px", // Match Fields.Input border radius if possible, usually 6px or 8px
            overflow: "hidden",
            backgroundColor: "#fff",
            height: "42px", // Fix height to align with other inputs
          }}
        >
          {/* Toggle Switcher */}
          <div
            onClick={() => {
              const currentType = variant.discountType || "percentage";
              const newType =
                currentType === "percentage" ? "flat" : "percentage";
              setFieldValue(`variants.${index}.discountType`, newType);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "50%",
              background: "#f8fafc",
              borderRight: "1px solid #e2e8f0",
              transition: "all 0.2s ease",
            }}
            title={`Click to switch to ${
              (variant.discountType || "percentage") === "percentage"
                ? "Flat Amount"
                : "Percentage"
            }`}
          >
            {(variant.discountType || "percentage") === "percentage" ? (
              <Percent size={14} color="#3b82f6" />
            ) : (
              <BadgeCent size={14} color="#10b981" />
            )}
          </div>

          {/* Input Field */}
          <div style={{ width: "50%", display: "flex", alignItems: "center" }}>
            <Fields.Input
              name={`variants.${index}.discount`}
              placeholder="0"
              styleInput="border: none; border-radius: 0; height: 100%; box-shadow: none; padding: 0 4px; text-align: center; text-indent: 0; font-size: 13px; &::placeholder { line-height: normal; }"
              type="number"
            />
          </div>
        </div>
      </td>
      <td style={{ padding: "8px 10px" }}>
        <Fields.Input name={`variants.${index}.stock`} placeholder="Unlimited" />
      </td>
      <td style={{ padding: "8px 10px" }}>
        <SkuInput
          variant={variant}
          index={index}
          onGenerateSku={onGenerateSku}
          setFieldValue={setFieldValue}
        />
      </td>
      <td style={{ padding: "8px 10px" }}>
        <Fields.Upload
          name={`variants.${index}.image`}
          height={60}
          styleWrapper="max-width:100px"
        />
      </td>
      <td style={{ padding: "8px 10px" }}>
        <button
          type="button"
          onClick={onRemove}
          style={{
            background: "transparent",
            border: "1px solid #e0e0e0",
            padding: "6px 10px",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Remove
        </button>
      </td>
    </tr>
  );
};

const SkuInput: React.FC<{
  variant: any;
  index: number;
  onGenerateSku: () => void;
  setFieldValue: (field: string, value: any) => void;
}> = ({ variant, index, onGenerateSku, setFieldValue }) => {
  return (
    <div style={{ flex: 1, position: "relative" }}>
      <Fields.Input name={`variants.${index}.sku`} placeholder="SKU" />
      <button
        type="button"
        onClick={onGenerateSku}
        style={{
          position: "absolute",
          top: "0",
          right: "0px",
          padding: "4px 8px",
          backgroundColor: theme.colors.primary,
          color: "white",
          border: "none",
          borderRadius: "6px",
          fontSize: "8px",
          fontWeight: "500",
          cursor: "pointer",
          transition: "all 0.2s ease",
          height: "39px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
        onMouseOver={(e: any) => {
          e.target.style.backgroundColor = theme.colors.primaryDark;
          e.target.style.transform = "translateY(-1px)";
          e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.15)";
        }}
        onMouseOut={(e: any) => {
          e.target.style.backgroundColor = theme.colors.primary;
          e.target.style.transform = "translateY(0)";
          e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
        }}
        onMouseDown={(e: any) => {
          e.target.style.transform = "translateY(0)";
          e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.1)";
        }}
        onMouseUp={(e: any) => {
          e.target.style.transform = "translateY(-1px)";
          e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.15)";
        }}
      >
        Generate
      </button>
    </div>
  );
};
